/**
 * FixtureCalendarView - Calendar/Scheduler view of fixtures
 * Displays fixtures on a grid with time slots and pitches matching Figma design
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import moment from "moment";
import { FiDownload, FiFilter, FiChevronDown, FiChevronUp, FiRotateCcw } from "react-icons/fi";

import TeamBadge from "../../components/TeamBadge";
import { DateNavigation, EmptyState, ViewToggle } from "../../components/Fixtures";
import { ReactComponent as EditIcon } from "../../assets/images/icons/edit.svg";

const FixtureCalendarView = ({
  fixturesByDate,
  sortedDates,
  showConflicts,
  selectedTournament,
  startDate,
  endDate,
  onDateChange,
  onShiftDates,
  isCalendarView,
  onToggleView,
  onOpenFilters,
  onOpenSwapTeams,
  onUndoSwap,
  undoableSwap,
  activeFiltersCount = 0,
  onGenerateFixtures,
  onCreateFixture,
  isReadOnly = false,
  canManageFixtures = true,
  canAddResults = true,
  lockedPhaseIds = new Set(),
  isOwner = false,
  onEditFixture,
  onDeleteFixture,
  onAddResult,
  onEditResult,
}) => {
  const [collapsedDates, setCollapsedDates] = useState({});

  const toggleDateCollapse = useCallback((date) => {
    setCollapsedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  }, []);

  // Generate time slots (9 AM to 8 PM, hourly)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 9; hour <= 20; hour++) {
      slots.push({
        hour,
        label: hour === 12 ? "12 PM" : hour < 12 ? `${hour} AM` : `${hour - 12} PM`,
        value: hour,
      });
    }
    return slots;
  }, []);

  // Get pitches from tournament settings, plus any fixture-only pitches
  const pitches = useMemo(() => {
    const pitchSet = new Set();

    // Start with tournament-configured pitches
    if (selectedTournament?.matchVenue?.pitches) {
      selectedTournament.matchVenue.pitches.forEach((pitch) => {
        if (typeof pitch === "string") {
          pitchSet.add(pitch);
        } else if (pitch?.name) {
          pitchSet.add(pitch.name);
        } else if (pitch?.label) {
          pitchSet.add(pitch.label);
        }
      });
    }

    // Also include pitches from fixtures (handles legacy/renamed pitches)
    if (pitchSet.size === 0) {
      Object.values(fixturesByDate).forEach((fixtures) => {
        fixtures.forEach((fixture) => {
          const pitch = fixture.pitch || fixture.pitchName || fixture.pitch_name;
          if (pitch) pitchSet.add(pitch);
        });
      });
    }

    if (pitchSet.size === 0 && Object.values(fixturesByDate).flat().length > 0) {
      pitchSet.add("Unassigned");
    }

    return Array.from(pitchSet).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, "")) || 0;
      const numB = parseInt(b.replace(/\D/g, "")) || 0;
      if (numA !== 0 && numB !== 0) return numA - numB;
      return a.localeCompare(b);
    });
  }, [fixturesByDate, selectedTournament]);

  // Build grid for a given array of fixtures
  const buildGrid = useCallback(
    (dateFixtures) => {
      const grid = {};
      timeSlots.forEach((slot) => {
        grid[slot.hour] = {};
        pitches.forEach((pitch) => {
          grid[slot.hour][pitch] = [];
        });
      });

      dateFixtures.forEach((fixture) => {
        // matchTime is now a UTC ISO instant — render in viewer-local TZ.
        const matchTimeStr = fixture.matchTime
          ? moment(fixture.matchTime).format("HH:mm")
          : fixture.match_time || fixture.time;
        let pitch = fixture.pitch || fixture.pitchName || fixture.pitch_name;

        if (!pitch && pitches.includes("Unassigned")) pitch = "Unassigned";

        if (matchTimeStr && pitch) {
          const hour24 = parseInt(String(matchTimeStr).split(":")[0]) || 0;
          let matchedSlot = timeSlots.find((s) => s.value === hour24);

          if (!matchedSlot && hour24 >= 9 && hour24 <= 20) {
            matchedSlot = timeSlots.reduce((closest, slot) => {
              return Math.abs(slot.value - hour24) < Math.abs(closest.value - hour24)
                ? slot
                : closest;
            });
          }

          if (matchedSlot) {
            // If pitch matches a column, use it; otherwise fall back to first column
            const resolvedPitch = grid[matchedSlot.hour]?.[pitch] ? pitch : pitches[0];
            if (resolvedPitch && grid[matchedSlot.hour]?.[resolvedPitch]) {
              grid[matchedSlot.hour][resolvedPitch].push(fixture);
            }
          }
        }
      });

      return grid;
    },
    [timeSlots, pitches]
  );

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes || "00"} ${ampm}`;
  };

  const exportToCSV = () => {
    const headers = ["Date", "Time", "Home Team", "Away Team", "Pitch", "Phase", "Group"];
    const rows = [];
    Object.entries(fixturesByDate).forEach(([date, fixtures]) => {
      fixtures.forEach((fixture) => {
        const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
        rows.push([
          startMoment ? startMoment.format("YYYY-MM-DD") : (date || ""),
          startMoment ? startMoment.format("HH:mm") : "",
          fixture.homeTeam?.title || fixture.homeTeam?.name || "TBD",
          fixture.awayTeam?.title || fixture.awayTeam?.name || "TBD",
          fixture.pitch || "",
          fixture.phaseName || "",
          fixture.groupName || "",
        ]);
      });
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? "")}"`).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `fixtures-${selectedTournament?.name || "export"}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const isEmpty = sortedDates.length === 0;

  return (
    <div className={isEmpty ? "flex-1 min-h-0 flex flex-col" : ""}>
      {/* Date Navigation & Actions Row */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:h-[44px] mb-6">
        <DateNavigation
          startDate={startDate ? new Date(startDate) : null}
          endDate={endDate ? new Date(endDate) : null}
          onDateChange={(start, end) => {
            if (start) onDateChange?.("start", moment(start).format("YYYY-MM-DD"));
            if (end) onDateChange?.("end", moment(end).format("YYYY-MM-DD"));
          }}
          onShiftDates={onShiftDates}
        />

        <div className="flex items-center gap-2 flex-wrap">
          {!isReadOnly && onOpenSwapTeams && (
            <button
              onClick={onOpenSwapTeams}
              className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
            >
              <span className="text-[#FEFCEB] font-medium text-[15px]">Team replacement</span>
            </button>
          )}

          {!isReadOnly && onUndoSwap && undoableSwap?.undoable && (
            <button
              onClick={onUndoSwap}
              title={
                undoableSwap.swap
                  ? `Undo: ${undoableSwap.swap.incomingTeamName} → ${undoableSwap.swap.outgoingTeamName}`
                  : "Undo last team replacement"
              }
              className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
            >
              <FiRotateCcw className="w-5 h-5 text-[#FEFCEB]" />
              <span className="text-[#FEFCEB] font-medium text-[15px]">Undo replacement</span>
            </button>
          )}

          <button
            onClick={exportToCSV}
            className="w-11 h-11 bg-app-card rounded-full flex items-center justify-center hover:opacity-50 transition-opacity"
            title="Download CSV"
          >
            <FiDownload className="w-5 h-5 text-[#FEFCEB]" />
          </button>

          <button
            onClick={onOpenFilters}
            className="h-11 px-4 bg-app-card rounded-full flex items-center gap-2 hover:opacity-50 transition-opacity"
          >
            <FiFilter className="w-5 h-5 text-[#FEFCEB]" />
            <span className="text-[#FEFCEB] font-medium text-[15px]">
              Filters{activeFiltersCount > 0 && ` (${activeFiltersCount})`}
            </span>
          </button>

          

          <ViewToggle isCalendar={isCalendarView} onToggle={onToggleView} />
        </div>
      </div>

      {/* Date Sections */}
      {sortedDates.length === 0 ? (
        <EmptyState
          onGenerateFixtures={onGenerateFixtures}
          onCreateFixture={onCreateFixture}
          hideButtons={isReadOnly}
        />
      ) : (
        <div className="space-y-4 mt-4">
          {sortedDates.map((date) => {
            const dateFixtures = fixturesByDate[date] || [];
            const isCollapsed = collapsedDates[date];
            const grid = isCollapsed ? null : buildGrid(dateFixtures);

            return (
              <div key={date} className="bg-app-surface p-3 rounded-2xl">
                {/* Collapsible heading */}
                <div
                  className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => toggleDateCollapse(date)}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
                    {isCollapsed ? (
                      <FiChevronDown className="w-5 h-5 text-[#FEFCEB]" />
                    ) : (
                      <FiChevronUp className="w-5 h-5 text-[#FEFCEB]" />
                    )}
                  </div>
                  <h3 className="text-[#FEFCEB] text-[16px] font-medium m-0">
                    {moment(date).format("ddd, D MMM")}
                  </h3>
                </div>

                {/* Calendar Grid */}
                {!isCollapsed && (
                  <div className="bg-[#000F1F] border border-[#192634] rounded-[20px] overflow-x-auto mt-4">
                    <div style={{ minWidth: `${80 + pitches.length * 220}px` }}>
                      {/* Grid Header - Pitches */}
                      <div
                        className="grid border-b border-[#192634]"
                        style={{ gridTemplateColumns: `80px repeat(${pitches.length}, 1fr)` }}
                      >
                        <div className="p-3 border-r border-[#192634]"></div>
                        {pitches.map((pitch) => (
                          <div
                            key={pitch}
                            className="p-3 border-r border-[#192634] last:border-r-0 text-center"
                          >
                            <h3 className="text-[#FEFCEB] text-[14px] font-semibold m-0">{pitch}</h3>
                          </div>
                        ))}
                      </div>

                      {/* Grid Body - Time Slots */}
                      {timeSlots.map((slot) => (
                        <div
                          key={slot.hour}
                          className="grid border-b border-[#192634] last:border-b-0 min-h-[100px]"
                          style={{ gridTemplateColumns: `80px repeat(${pitches.length}, 1fr)` }}
                        >
                          <div className="p-3 border-r border-[#192634] flex items-center">
                            <span className="text-[#FEFCEB] text-[14px] font-semibold">
                              {slot.label}
                            </span>
                          </div>

                          {pitches.map((pitch) => {
                            const fixtures = grid[slot.hour]?.[pitch] || [];
                            return (
                              <div
                                key={pitch}
                                className="p-1.5 border-r border-[#192634] last:border-r-0 min-h-[100px] flex flex-col"
                              >
                                {fixtures.map((fixture) => (
                                  <CalendarFixtureCard
                                    key={fixture._id || fixture.id}
                                    fixture={fixture}
                                    formatTime={formatTime}
                                    isReadOnly={isReadOnly}
                                    canManageFixtures={canManageFixtures}
                                    canAddResults={canAddResults}
                                    isLocked={lockedPhaseIds.has(fixture.phaseId)}
                                    isOwner={isOwner}
                                    onEdit={onEditFixture}
                                    onDelete={onDeleteFixture}
                                    onAddResult={onAddResult}
                                    onEditResult={onEditResult}
                                    pitches={pitches}
                                  />
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Compact fixture card for calendar grid cells
 */
const CalendarFixtureCard = ({
  fixture,
  formatTime,
  isReadOnly,
  canManageFixtures,
  canAddResults,
  isLocked,
  isOwner,
  onEdit,
  onDelete,
  onAddResult,
  onEditResult,
  pitches
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isFinal = fixture.status === "COMPLETED" || fixture.hasResult;
  const isPending = !isFinal;
  const phaseName = fixture.phaseName || "Phase";
  const groupName = fixture.groupName || "";
  const homeTitle = fixture.homeTeam?.title || fixture.homeTeam?.name || fixture.homeTeamName || "TBD";
  const awayTitle = fixture.awayTeam?.title || fixture.awayTeam?.name || fixture.awayTeamName || "TBD";

  const fixtureStart = fixture.matchTime ? moment(fixture.matchTime) : null;
  // Compare by DATE only — a fixture later today still counts as "today".
  const isFuture = fixtureStart?.isValid() ? fixtureStart.isAfter(moment(), 'day') : false;

  const handleResultAction = () => {
    if (isPending && onAddResult) onAddResult(fixture);
    else if (isFinal && onEditResult) onEditResult(fixture);
  };

  return (
    <div className="bg-[#000F1F] border border-[#192634] rounded-2xl mb-1.5 last:mb-0 flex flex-col hover:border-[#00E7FF]/50 transition-colors overflow-hidden">
      {/* Top bar — phase/group + menu */}
      <div className="flex items-center justify-between px-2 pt-1.5">
        <span className="text-[9px] text-app-text-secondary truncate">
          {phaseName}{groupName ? ` - ${groupName}` : ""}
        </span>
        {!isReadOnly && canManageFixtures && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-0.5 hover:bg-white/10 rounded-full transition-colors"
            >
              <EditIcon className="w-3.5 h-3.5 text-[#FEFCEB]" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-28 bg-[#000F1F] border border-[#192634] rounded-xl shadow-xl z-50 overflow-hidden">
                <button
                  onClick={() => { onEdit?.(fixture); setIsMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-[#FEFCEB] hover:bg-[#0D1B29] transition-colors text-xs font-medium border-b border-[#192634]"
                >
                  Edit
                </button>
                <button
                  onClick={() => { onDelete?.(fixture); setIsMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-app-error hover:bg-[#0D1B29] transition-colors text-xs font-medium"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teams + Score/Time */}
      <div className="flex items-center justify-between px-2 py-1.5">
        <div className="flex flex-col gap-1 items-center justify-center w-[72px] shrink-0">
          <TeamBadge
            src={fixture.homeTeam?.crest || fixture.homeTeam?.logo}
            alt={homeTitle}
            containerSize="w-9 h-9"
            size="w-3 h-3"
          />
          <p className="text-[#FEFCEB] text-[10px] font-semibold text-center leading-tight m-0 w-full min-w-0 truncate">
            {homeTitle}
          </p>
        </div>

        <div className="flex flex-col items-center justify-center flex-1 min-w-[36px]">
          {isFinal ? (
            <p className="text-[#FEFCEB] text-sm font-bold m-0">
              {fixture.homeTeamScore ?? 0} - {fixture.awayTeamScore ?? 0}
            </p>
          ) : (
            <div className="text-center">
              <p className="text-[#FEFCEB] text-[10px] font-medium m-0">VS</p>
              <p className="text-app-text text-[10px] m-0 whitespace-nowrap">
                {formatTime(fixture.matchTime ? moment(fixture.matchTime).format("HH:mm") : "")}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1 items-center justify-center w-[72px] shrink-0">
          <TeamBadge
            src={fixture.awayTeam?.crest || fixture.awayTeam?.logo}
            alt={awayTitle}
            containerSize="w-9 h-9"
            size="w-3 h-3"
          />
          <p className="text-[#FEFCEB] text-[10px] font-semibold text-center leading-tight m-0 w-full min-w-0 truncate">
            {awayTitle}
          </p>
        </div>
      </div>

      {/* Action button */}
      <div className="flex justify-center items-center w-full">

        {canAddResults && !(isPending && isFuture) && (!isLocked || isOwner) && (
          <button
            onClick={handleResultAction}
            className={`w-[100%]  ${pitches?.length < 3 && "max-w-[200px]"} mx-1.5 mb-1.5 py-1.5 bg-app-card text-[#FEFCEB] rounded-full hover:opacity-50 transition-opacity font-medium text-[11px]`}
          >
            {isPending ? "Add result" : "Edit result"}
          </button>
        )}
      </div>

    </div>
  );
};

export default FixtureCalendarView;
